import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ICreateInstallation,
  IInstallation,
  installationStatus,
  installationStatusMigration,
} from '@modules/clients/models/installation';
import { IInstaller, IMunicipality, IState, IZone } from '@modules/clients/models/location';
import { IInstallationUpdate } from '@modules/clients/pages/new-client/new-client.component';
import { IResponse, IResponsePaginationBack } from '@sharedModels/response-pagination.model';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { GlobalStateService } from 'src/app/shared/services/global-state/global-state.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class InstallationService {
  counter = {
    dc: 0,
    fd: 0,
    fmd: 0,
  };
  constructor(
    private readonly http: HttpClient,
    private readonly toastService: ToastrService,
    private readonly stateService: GlobalStateService,
    public confirmationService: ConfirmationService,
  ) {}

  async getAllStates(): Promise<IState[]> {
    return firstValueFrom(this.http.get<IState[]>(`${environment.apiUrl}/location/states`));
  }

  async getMunicipalitiesByState(stateId: number): Promise<IMunicipality[]> {
    return firstValueFrom(
      this.http.get<IMunicipality[]>(`${environment.apiUrl}/location/municipalities/${stateId}`),
    );
  }

  async getZoneByMunicipalities(munId: number): Promise<IZone[]> {
    return firstValueFrom(
      this.http.get<IZone[]>(`${environment.apiUrl}/zones/municipality/${munId}`),
    );
  }

  async getAllInstallers(): Promise<IInstaller[]> {
    return firstValueFrom(this.http.get<IInstaller[]>(`${environment.apiUrl}/installer`));
  }

  async createInstallation(installation: ICreateInstallation): Promise<IResponse<IInstallation>> {
    return firstValueFrom(
      this.http.post<any>(`${environment.apiUrl}/installation/create`, installation),
    );
  }

  async updateInstallation(
    id: number,
    installation: ICreateInstallation | IInstallationUpdate,
  ): Promise<any> {
    return firstValueFrom(
      this.http.put<any>(`${environment.apiUrl}/installation/${id}`, installation),
    );
  }

  async getAllNotApceptedInstallations(
    band: boolean,
    queryParam?: { companyOwner: number },
  ): Promise<IResponse<any[]>> {
    return firstValueFrom(
      this.http.get<IResponse<any[]>>(`${environment.apiUrl}/installation/accepted/${band}`, {
        params: queryParam,
      }),
    );
  }

  async getAllMigrateInstallations(queryParam?: {
    companyOwner: number;
  }): Promise<IResponse<any[]>> {
    return firstValueFrom(
      this.http.get<IResponse<any[]>>(`${environment.apiUrl}/installation/get-migrated`, {
        params: queryParam,
      }),
    );
  }

  async acceptInstallation(id: number): Promise<any> {
    return firstValueFrom(
      this.http.patch<any>(`${environment.apiUrl}/installation/${id}/accept/${true}`, {}),
    );
  }

  async deleteInstallation(id: number): Promise<any> {
    return firstValueFrom(this.http.delete<any>(`${environment.apiUrl}/installation/${id}`));
  }

  async getAllQuadrilles(companyOwner: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any[]>(`${environment.apiUrl}/quadrilles`, {
        params: { companyOwner },
      }),
    );
  }

  async getAllNAPBox(): Promise<any> {
    return firstValueFrom(this.http.get<any[]>(`${environment.apiUrl}/box-nap`));
  }

  /**
   * getInstallationById
   */
  public async getInstallationById(id: number): Promise<IResponse<IInstallation>> {
    return firstValueFrom(
      this.http.get<IResponse<IInstallation>>(`${environment.apiUrl}/installation/${id}`),
    );
  }

  public async acceptInstallationArray(ids: Array<number>): Promise<IResponse<IInstallation[]>> {
    return firstValueFrom(
      this.http.patch<IResponse<IInstallation[]>>(
        `${environment.apiUrl}/installation/accept-array`,
        ids,
      ),
    );
  }

  public async setInstallationStatus(
    id: number,
    status: installationStatus,
  ): Promise<IResponse<IInstallation>> {
    return firstValueFrom(
      this.http.patch<IResponse<IInstallation>>(
        `${environment.apiUrl}/installation/${id}/status/${status}`,
        {},
      ),
    );
  }

  /**
   * getInstallationsByStatus
   */
  public async getInstallationsByStatus(
    status: installationStatus,
    queryParam?: { companyOwner: number },
  ): Promise<IInstallation[]> {
    const list = await this.fetchInstallations(queryParam);
    return list.filter((installation) => installation.status === status);
  }

  /**
   * getAllInstallations
   * Listado completo de instalaciones de la compañía (sin filtrar por estado).
   */
  public async getAllInstallations(companyOwner: number): Promise<IInstallation[]> {
    return this.fetchInstallations({ companyOwner });
  }

  private async fetchInstallations(queryParam?: {
    companyOwner: number;
  }): Promise<IInstallation[]> {
    const response = await firstValueFrom(
      this.http.get<IInstallation[] | Record<string, IInstallation> | IResponse<IInstallation[]>>(
        `${environment.apiUrl}/installation`,
        { params: queryParam },
      ),
    );
    const raw = response as any;
    const payload = Array.isArray(raw) ? raw : raw?.data ?? raw;
    return Array.isArray(payload) ? payload : Object.values(payload);
  }

  checkInstallationData(installation: any): installationStatusMigration {
    const parameters = [
      'address',
      'boxNap',
      'boxNapId',
      'cellphone',
      'email',
      'fullName',
      'ip',
      'mac',
      'portNapId',
      'zone',
      'zoneId',
      // 'planId',
      // 'routerId',
    ];

    const totalParameters = parameters.length;
    let missingCount = 0;

    parameters.forEach((param) => {
      if (!installation[param]) {
        missingCount++;
      }
    });

    if (installation.zone && installation.zone.zoneAddress === 'Invalid Zone') {
      missingCount++;
    }

    const missingPercentage = (missingCount / totalParameters) * 100;

    if (missingPercentage > 40) {
      this.counter.fmd++;
      return installationStatusMigration.A_LOT_MISSING;
    } else if (missingCount > 0) {
      this.counter.fd++;
      return installationStatusMigration.SOME_MISSING;
    } else {
      this.counter.dc++;
      return installationStatusMigration.COMPLETE;
    }
  }

  resetCounter() {
    this.counter = {
      dc: 0,
      fd: 0,
      fmd: 0,
    };

    return this.counter;
  }

  getCounter() {
    return this.counter;
  }

  public async getInstPlans(companyOwner: number): Promise<IResponse<any[]>> {
    return firstValueFrom(
      this.http.get<IResponse<any[]>>(`${environment.apiUrl}/plans`, {
        params: { companyOwner },
      }),
    );
  }

  public async getInstRouters(companyOwner: number): Promise<IResponse<any[]>> {
    return firstValueFrom(
      this.http.get<IResponse<any[]>>(`${environment.apiUrl}/router/get-all`, {
        params: { companyOwner },
      }),
    );
  }

  /**
   * getBillInstallation
   */
  public getBillInstallation(id: number): Promise<IResponse<any>> {
    return firstValueFrom(
      this.http.get<IResponse<any>>(`${environment.apiUrl}/installation/${id}/payment-link`),
    );
  }

  public async generateInstallationBill(evt: IInstallation): Promise<any> {
    this.stateService.setShowLoading(true);
    return this.getBillInstallation(evt.id)
      .then(async (res) => {
        // this.copyText(res.data);
        this.toastService.success('Enlace de pago generado correctamente', 'Éxito');
        (evt as any).path = res.data;
        if (!sessionStorage.getItem('PaidInstallations')) {
          sessionStorage.setItem('PaidInstallations', JSON.stringify([]));
        }
        if (sessionStorage.getItem('PaidInstallations')) {
          const installations: IInstallation[] = JSON.parse(
            sessionStorage.getItem('PaidInstallations') || '[]',
          );
          const finded = installations.find((ins) => ins.id === evt.id);
          if (!finded) {
            installations.push(evt);
          }
          sessionStorage.setItem('PaidInstallations', JSON.stringify(installations));
        }
      })
      .finally(() => {
        this.stateService.setShowLoading(false);
      });
  }

  public setPathIfExists(originalInstallations: IInstallation[]) {
    if (sessionStorage.getItem('PaidInstallations')) {
      const installations: IInstallation[] = JSON.parse(
        sessionStorage.getItem('PaidInstallations') || '[]',
      );
      const find = installations.find((ins) => originalInstallations.some((i) => i.id === ins.id));
      if (find) {
        for (let item of originalInstallations) {
          if (item.id === find.id) {
            (item as any).path = (find as any).path;
          }
        }
      }
    }
  }

  /**
   * copuyText
   */
  public copyText(path: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(path).then(
        () => {
          this.toastService.success('Texto copiado al portapapeles', 'Éxito');
        },
        (err) => {
          this.toastService.error('Error al copiar el texto', 'Error');
        },
      );
    }
  }

  public validateGenerateBill(evt: IInstallation) {
    this.confirmationService.confirm({
      message: `¿Está seguro de generar  el <strong>link de cobro</strong> para la instalación <strong>${evt.id}</strong> por un monto de  <strong>${evt.price} USD</strong>?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        await this.generateInstallationBill(evt);
      },
    });
  }

  async getInstallationsPaginated(queryParam: {
    companyOwner: number;
    page: number;
    limit: number;
    globalFilter: string;
    checkAccepted?: boolean;
    isMigrated?: boolean;
  }) {
    return await firstValueFrom(
      this.http.get<IResponsePaginationBack<IInstallation>>(
        `${environment.apiUrl}/installation/paginated`,
        {
          params: queryParam,
        },
      ),
    );
  }

  /**
   * exportSelectedInstallationsExcel
   * @param selectedIds Array of selected installation IDs
   * @param companyOwner Company owner ID
   * @param globalFilter Optional search filter
   * @param exportAll Boolean to export all installations
   * @param accepted Optional filter for accepted installations
   * @param isMigrated Optional filter for migrated installations
   * @returns Blob of the Excel file
   */
  public async exportSelectedInstallationsExcel(
    selectedIds: number[],
    companyOwner: number,
    globalFilter: string = '',
    exportAll: boolean = false,
    accepted?: boolean,
    isMigrated?: boolean,
  ): Promise<Blob> {
    const params: any = {
      companyOwner,
      globalFilter,
      selectedIds: selectedIds.join(','),
      exportAll,
      accepted,
      isMigrated,
    };

    return await firstValueFrom(
      this.http.get(`${environment.apiUrl}/installation/export-excel`, {
        params,
        responseType: 'blob',
      }),
    );
  }

  /**
   * exportSelectedClientsToCsv
   * @param selectedIds Array of selected client IDs
   * @param companyOwner Company owner ID
   * @param globalFilter Optional search filter
   * @param exportAll Boolean to export all clients
   * @returns Blob of the CSV file
   */
  public async exportSelectedInstallationsToCsv(
    selectedIds: number[],
    companyOwner: number,
    globalFilter: string = '',
    exportAll: boolean = false,
    accepted?: boolean,
    isMigrated?: boolean,
  ): Promise<Blob> {
    const params: any = {
      companyOwner,
      globalFilter,
      selectedIds: selectedIds.join(','),
      exportAll,
      accepted,
      isMigrated,
    };
    return await firstValueFrom(
      this.http.get(`${environment.apiUrl}/installation/export-csv`, {
        params,
        responseType: 'blob',
      }),
    );
  }

  /**
   * exportSelectedClientsToPdf
   * @param selectedIds Array of selected client IDs
   * @param companyOwner Company owner ID
   * @param globalFilter Optional search filter
   * @param exportAll Boolean to export all clients
   * @returns Blob of the PDF file
   */
  public async exportSelectedInstallationsToPdf(
    selectedIds: number[],
    companyOwner: number,
    globalFilter: string = '',
    exportAll: boolean = false,
    accepted?: boolean,
    isMigrated?: boolean,
  ): Promise<Blob> {
    const params: any = {
      companyOwner,
      globalFilter,
      selectedIds: selectedIds.join(','),
      exportAll,
      accepted,
      isMigrated,
    };
    return await firstValueFrom(
      this.http.get(`${environment.apiUrl}/installation/export-pdf`, {
        params,
        responseType: 'blob',
      }),
    );
  }
}
